import { Row } from 'antd';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {getScaleLabels, Scale, ScaleVisualization} from '~src/interfaces/scale.interface';
import { Topic } from '~src/interfaces/topic.interface';
import {TopicCreate, editTopicPatch} from '~src/modules/topics/actions/topicActions';
import { StoreI } from '~src/rootReducer';
import { translateContent } from '~src/services/translationServices';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import ScaleSuper from '~src/shared/features/scale/Scale';
import Checkbox, { CheckBoxType } from '~src/shared/form/Checkbox';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import { MainContainerAnimation } from '~src/styles/animations/containerAnimations.sc';

import {
  ButtonSection,
  CancelBtn,
  ChangeScaleToggle,
  HeaderContainer,
  IconContainer,
  QuestionListHeader,
  ScaleContainer,
  ScaleDescription, ScaleName,
} from './topicScaleAdd.sc';
import {isSuccess, ThunkAnyAction} from "~src/services/apiMiddleware";

interface OwnProps {
  scales: { [key: string]: Scale };
  selectedTopic: Topic;
}

interface StateProps {
  loadingEdit: boolean;
}

interface DispatchProps {
  editTopicPatch(topicId: string, body: TopicCreate): Promise<ThunkAnyAction<Topic>>;
}

type Props = OwnProps & StateProps & DispatchProps & WithTranslation;

interface State {
  scales?: { [key: string]: Scale };
  selectedScaleId: string;
  scaleToggled: boolean;
  topic: Topic;
  showButtons: boolean;
}

interface DefaultProps {
  scales: { [key: string]: Scale };
}

class TopicScaleAdd extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    scales: {},
  };

  state = {
    scales: this.props.scales,
    selectedScaleId: this.props.scales[this.props.selectedTopic.scaleId].id,
    scaleToggled: false,
    topic: this.props.selectedTopic,
    showButtons: false,
  };

  changeSelectedScale = (_: boolean, val: string) => {
    const { scales, selectedTopic } = this.props;
    this.setState({ selectedScaleId: val });
    this.setState({ showButtons: val !== scales[selectedTopic.scaleId].id });
  };

  toggleScales = () => {
    this.setState((prevState) => ({ scaleToggled: !prevState.scaleToggled }));
  };

  saveScale = () => {
    void this.props
      .editTopicPatch(this.props.selectedTopic.id, {
        scaleId: this.state.selectedScaleId,
        name: this.state.topic.name,
      })
      .then((response) => {
        if (isSuccess(response)) {
          this.setState({topic: response.response.data});
          this.setState({scaleToggled: false});
          this.setState({showButtons: false});
        }
      });
  };

  render() {
    const { t } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const labels = getScaleLabels(this.state.scales[this.state.topic.scaleId]);

    return (
      <Row>
        <MainContainerAnimation>
          <QuestionListHeader empty={Object.keys(this.state.scales).length < 1}>
            <HeaderContainer>
              <span data-translation="x.topics.details.scale.list.header.title">
                {t('x.topics.details.scale.list.header.title')}
              </span>
              <IconContainer>
                <HoverInfoIcon
                  infoText={{
                    key: 'x.topics.details.scale.list.header.textbox',
                    txt: t('x.topics.details.scale.list.header.textbox'),
                  }}
                />
              </IconContainer>
              {!this.state.scaleToggled && (
                <ChangeScaleToggle
                  onClick={this.toggleScales}
                  data-translation="x.topics.details.scale.list.header.toggle"
                >
                  {t('x.topics.details.scale.list.header.toggle')}
                </ChangeScaleToggle>
              )}
            </HeaderContainer>
            {!this.state.scaleToggled && (
              <ScaleName>{translateContent(this.props.scales[this.state.topic.scaleId].name)}</ScaleName>
            )}
          </QuestionListHeader>
          {!this.state.scaleToggled && (
            <ScaleSuper
              t={t}
              type={this.props.scales[this.state.topic.scaleId].visualization}
              labels={labels}
              isPreview
              inContainer
              isLast
              rating={null}
              isMultipleChoiceEnabled={this.props.scales[this.state.topic.scaleId].isMultipleChoiceEnabled}
            />
          )}
          {this.state.scaleToggled && (
            <div>
              {Object.keys(this.state.scales).map((key: string) => (
                <div key={this.state.scales[key].id}>
                  <ScaleContainer>
                    <Checkbox
                      id={`radio_${this.state.scales[key].id}`}
                      type={CheckBoxType.Radio}
                      name="scale"
                      onChange={this.changeSelectedScale}
                      val={this.state.scales[key].id}
                      isChecked={this.state.selectedScaleId === this.state.scales[key].id}
                      label={translateContent(this.state.scales[key].name)}
                      labelKey="coming from pulse check service framework.json"
                    />
                    <ScaleDescription data-translation="coming from pulse check service framework.json">
                      {this.state.scales[key].description
                        ? translateContent(this.state.scales[key].description)
                        : null}
                    </ScaleDescription>
                  </ScaleContainer>
                  {this.state.selectedScaleId === this.state.scales[key].id && (
                    <ScaleSuper
                      t={t}
                      type={this.state.scales[key].visualization}
                      labels={getScaleLabels(this.state.scales[key])}
                      inContainer
                      rating={null}
                      isPreview
                      isMultipleChoiceEnabled={this.state.scales[key].isMultipleChoiceEnabled}
                    />
                  )}
                </div>
              ))}
              {this.state.showButtons && (
                <ButtonSection>
                  <div className="cancel-btn-wrapper">
                    <CancelBtn onClick={this.toggleScales}>
                      <span data-translation="x.button.cancel">{t('x.button.cancel')}</span>
                    </CancelBtn>
                  </div>
                  <PrimaryButtonLoading onClick={this.saveScale} isLoading={this.props.loadingEdit}>
                    <span data-translation="x.button.save">{t('x.button.save')}</span>
                  </PrimaryButtonLoading>
                </ButtonSection>
              )}
            </div>
          )}
        </MainContainerAnimation>
      </Row>
    );
  }
}

const mapStateToProps = ({ topics }: StoreI): StateProps => ({
  loadingEdit: topics.loadingEdit,
});

const mapDispatchToProps = (dispatch): DispatchProps => {
  return {
    editTopicPatch: (topicId, body): Promise<ThunkAnyAction<Topic>> => dispatch(editTopicPatch(topicId, body)),
  };
};

const TopicScaleAddWithTranslate = withTranslation()(TopicScaleAdd);
const TopicScaleAddSuperComponent = connect(mapStateToProps, mapDispatchToProps)(TopicScaleAddWithTranslate);

export default TopicScaleAddSuperComponent;
